import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import {
    environment,
    GlobalSearchProvider,
    GlobalStateProvider,
    GoogleAnalyticsProvider,
    MissingTranslationProvider,
    ReactQueryContainer,
    SidebarProvider,
    ToastProvider,
    useFeatureFlags,
    useToast,
} from "@sonic-equipment/ui";
import CookieBot from "@sonic/CustomComponents/CookieBot/Cookiebot";
import { ReduxCartProvider } from "@sonic/Providers/ReduxCartProvider";
// eslint-disable-next-line spire/fenced-imports
import { connect } from "react-redux";

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import * as Sentry from "@sentry/react";
import { ReduxFavoriteProvider } from "@sonic/Providers/ReduxFavoriteProvider";
import { ReduxRouteProvider } from "@sonic/Providers/ReduxRouteProvider";
import { SpireIntlProvider } from "@sonic/Providers/SpireIntlProvider";
import { searchClient } from "@sonic/Services/algoliaSonicClient";
import "@sonic/Services/loggingService";
import * as React from "react";
import { ReactNode } from "react";
import styled, { css } from "styled-components";

if (!IS_SERVER_SIDE) {
    /* eslint-disable global-require */
    require("@sonic-equipment/ui/fonts.css");
    require("@sonic-equipment/ui/styles.css");
    /* eslint-enable global-require */

    if (environment === "production" || environment === "sandbox") {
        Sentry.init({
            dsn: "https://1af8ff246a5def67beb82451f7e937bb@o4507894794813440.ingest.de.sentry.io/4507894797041744",
            integrations: [
                // Sentry.browserTracingIntegration(),
                // Sentry.browserProfilingIntegration(),
                // Sentry.replayIntegration(),
            ],

            environment: environment || "unknown",
            // Tracing
            tracesSampleRate: 1.0, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [`${window.location.hostname}:${window.location.port}`],

            // // Set profilesSampleRate to 1.0 to profile every transaction.
            // // Since profilesSampleRate is relative to tracesSampleRate,
            // // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
            // // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
            // // results in 25% of transactions being profiled (0.5*0.5=0.25)
            // profilesSampleRate: 1.0,

            // // Session Replay
            // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        });
    }
}

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

const PageBreadcrumbContainerStyles = css`
    max-width: 1440px;
    padding: 15px;
`;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const page = getCurrentPage(state);
    const session = getSession(state);
    const billTo = getCurrentBillToState(state);

    return {
        languageCode: session.language?.cultureCode,
        currencyCode: session.currency?.currencyCode,
        page,
        location,
    };
};

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps>;

const PrintLayout = styled.div`
    @media print {
        main {
            [class^="page-container-module"] {
                &:has(h1) {
                    padding: 0;
                    margin-bottom: 1rem;
                }

                h1 {
                    font-size: 32px !important;
                    font-style: normal !important;
                    text-transform: none !important;
                }

                &:has(ol[class^="breadcrumb-module"]) {
                    display: none;
                }
            }

            section[class^="checkout-page-section-module"] {
                padding: 0;

                > h2 {
                    font-size: 24px;
                }
            }
        }

        [class^="Wrapper--"] {
            display: none;
        }

        [class^="FluidContainer--"] {
            display: none;
        }

        [class^="checkout-page-layout-module"] {
            border: none;
            padding: 0;

            > [class^="cart-totals-module"] {
                page-break-before: always;
                break-before: always;
            }

            [class^="button-module"] {
                display: none;
            }
        }
    }
`;

// This doesn't currently support HMR
const PageLayout = ({
    addressBookOverlay,
    compactHeader,
    footer,
    header,
    location,
    page,
    pageContent,
    showAddressBookOverlay,
    showBreadcrumbs,
    showCompactHeader,
    showFooter,
    showHeader,
    showSignInOverlay,
    showStorePickupOverlay,
    signInOverlay,
    storePickupOverlay,
    languageCode,
    currencyCode,
}: Props) => {
    const { plpV1, cartV1, checkoutV1 } = useFeatureFlags();

    const isCheckout = Boolean(location.pathname.match(/checkout|cart|checkoutreviewandsubmit/i));
    const mustHideBreadCrumbForNewPage = Boolean(
        (!plpV1 && page.type.match("ProductListPage|CategoryDetailsPage")) ||
            (!cartV1 && page.type.match("CartPage")) ||
            (!checkoutV1 && page.type.match("CheckoutShipping|CheckoutReviewAndSubmit|OrderConfirmation")),
    );
    const { addToast } = useToast();

    const pageLayout = (
        <ReactQueryContainer>
            <SpireIntlProvider languageCode={languageCode} currencyCode={currencyCode}>
                <MissingTranslationProvider>
                    <GlobalStateProvider>
                        <ToasterContext.Provider
                            value={{
                                addToast: props => {
                                    addToast({
                                        body: props.body,
                                        messageType: props.messageType,
                                        isUserDismissable: false,
                                    });
                                },
                                removeToast: (...args) => console.log("removeToast", args),
                                defaultTimeout: 3000,
                            }}
                        >
                            <ReduxCartProvider />
                            <ReduxFavoriteProvider />
                            <ReduxRouteProvider location={location}>
                                <GoogleAnalyticsProvider>
                                    <SidebarProvider>
                                        <GlobalSearchProvider searchClient={searchClient}>
                                            <ToastProvider />
                                            <div className="body">
                                                {showHeader && header}
                                                {showCompactHeader && compactHeader}
                                                <div className="main">
                                                    {showBreadcrumbs && !mustHideBreadCrumbForNewPage && (
                                                        <StyledWrapper {...PageBreadcrumbContainerStyles}>
                                                            <PageBreadcrumbs />
                                                        </StyledWrapper>
                                                    )}
                                                    {pageContent}
                                                </div>
                                                {showFooter && footer}
                                                {showSignInOverlay && signInOverlay}
                                                {showStorePickupOverlay && storePickupOverlay}
                                                {showAddressBookOverlay && addressBookOverlay}
                                                {!isCheckout && <CookieBot></CookieBot>}
                                            </div>
                                        </GlobalSearchProvider>
                                    </SidebarProvider>
                                </GoogleAnalyticsProvider>
                            </ReduxRouteProvider>
                        </ToasterContext.Provider>
                    </GlobalStateProvider>
                </MissingTranslationProvider>
            </SpireIntlProvider>
        </ReactQueryContainer>
    );

    return checkoutV1 ? pageLayout : <PrintLayout>{pageLayout}</PrintLayout>;
};

export default connect(mapStateToProps)(PageLayout);
